import {
  Box,
  Button,
  Switch as ChakraSwitch,
  Grid,
  GridItem,
  HStack,
  Heading,
  Input,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  type DropResult,
  Droppable,
  type DroppableProvided,
} from "@hello-pangea/dnd"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import { type ReactNode, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { FaUpload } from "react-icons/fa"
import { MdDragHandle } from "react-icons/md"

import {
  ContentService,
  FlashcardDecksService,
  type FlashcardPublic,
  ProgramsService,
  type QuestionPublic,
  QuestionsService,
} from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import {
  type ProgramOption,
  ProgramSelector,
} from "@/components/Programs/ProgramSelector"
import { BulkUploadErrorModal } from "@/components/Questions/BulkUploadErrorModal"
import { SubjectSelector } from "@/components/Questions/SubjectSelector"
import { QuizQuestionBank } from "@/components/Quizzes/QuizQuestionBank"
import { TagInput } from "@/components/Quizzes/TagInput"
import { QuestionPreviewModal } from "@/components/topics/QuestionPreviewModal"
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import { RichTextEditor } from "@/components/ui/rich-text-editor"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"
import { containsLocalImageUrls } from "@/utils/richTextImages"
import { FlashcardContent } from "./FlashcardContent"

interface DeckFormValues {
  name: string
  description: string
}

interface PendingCard {
  front: string
  back: string
  source_question_id?: string | null
}

export interface FlashcardDeckBuilderProps {
  mode: "create" | "edit"
  deckId?: string
}

interface FlashcardBulkUploadDialogProps {
  deckId: string
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")

const buildBackFromQuestion = (question: QuestionPublic) => {
  const answer = question.options[question.correct_answer] ?? ""
  const rationale = question.rationale?.trim()
  return [
    `<p><strong>Answer:</strong> ${escapeHtml(answer)}</p>`,
    rationale ? `<div>${rationale}</div>` : "",
  ]
    .filter(Boolean)
    .join("")
}

function FlashcardBulkUploadDialog({ deckId }: FlashcardBulkUploadDialogProps) {
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadErrors, setUploadErrors] = useState<
    Array<{ row: number; error: string }>
  >([])
  const [errorModalOpen, setErrorModalOpen] = useState(false)

  const mutation = useMutation({
    mutationFn: (file: File) =>
      FlashcardDecksService.bulkUploadCards({
        deckId,
        formData: { file },
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["flashcard-deck-cards", deckId],
      })
      queryClient.invalidateQueries({ queryKey: ["flashcard-decks"] })
      if (data.error_count > 0) {
        setUploadErrors(data.errors ?? [])
        setErrorModalOpen(true)
        showSuccessToast(
          `Uploaded ${data.success_count} flashcards. ${data.error_count} errors.`,
        )
      } else {
        showSuccessToast(
          `Successfully uploaded ${data.success_count} flashcards.`,
        )
      }
      setSelectedFile(null)
      setIsOpen(false)
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const handleDownloadTemplate = () => {
    const csv = [
      ["front", "back"].join(","),
      [
        "What is preload?",
        "End-diastolic stretch of the ventricle before contraction",
      ]
        .map((value) => `"${value.replace(/"/g, '""')}"`)
        .join(","),
    ].join("\n")

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "flashcards-bulk-upload-template.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <DialogRoot
        size={{ base: "xs", md: "md" }}
        placement="center"
        open={isOpen}
        onOpenChange={({ open }) => setIsOpen(open)}
      >
        <DialogTrigger asChild>
          <Button size="sm" variant="outline">
            <FaUpload fontSize="14px" />
            Upload CSV
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Upload Flashcards</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={4} align="stretch">
              <Text>
                Upload a CSV file with flashcards. Required columns: front,
                back. Uploaded rows are saved directly into this deck and are
                not added to the question bank.
              </Text>
              <Button
                alignSelf="flex-start"
                size="sm"
                variant="subtle"
                onClick={handleDownloadTemplate}
              >
                Download sample CSV
              </Button>
              <Field label="Choose CSV File">
                <Input
                  type="file"
                  accept=".csv"
                  aria-label="Choose CSV File"
                  onChange={(event) =>
                    setSelectedFile(event.target.files?.[0] ?? null)
                  }
                />
              </Field>
              {selectedFile && (
                <Text fontSize="sm" color="gray.500">
                  Selected: {selectedFile.name}
                </Text>
              )}
            </VStack>
          </DialogBody>
          <DialogFooter gap={2}>
            <DialogActionTrigger asChild>
              <Button variant="subtle" colorPalette="gray">
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              onClick={() => selectedFile && mutation.mutate(selectedFile)}
              disabled={!selectedFile}
              loading={mutation.isPending}
            >
              Upload
            </Button>
          </DialogFooter>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
      <BulkUploadErrorModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        errors={uploadErrors}
      />
    </>
  )
}

export function FlashcardDeckBuilder({
  mode,
  deckId,
}: FlashcardDeckBuilderProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [selectedPrograms, setSelectedPrograms] = useState<ProgramOption[]>([])
  const [deckSubject, setDeckSubject] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [deckTags, setDeckTags] = useState<string[]>([])
  const [pendingCards, setPendingCards] = useState<PendingCard[]>([])
  const [searchValue, setSearchValue] = useState("")
  const [selectedSubjects, setSelectedSubjects] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [selectedTopics, setSelectedTopics] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [previewQuestionId, setPreviewQuestionId] = useState<string | null>(
    null,
  )
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [shuffleCards, setShuffleCards] = useState(true)

  // Manual card form state
  const [manualFront, setManualFront] = useState("")
  const [manualBack, setManualBack] = useState("")
  const [manualError, setManualError] = useState("")
  const [richTextUploadCount, setRichTextUploadCount] = useState(0)
  const isUploadingRichTextImage = richTextUploadCount > 0
  const handleRichTextUploadStateChange = (isUploading: boolean) => {
    setRichTextUploadCount((count) =>
      Math.max(0, count + (isUploading ? 1 : -1)),
    )
  }

  // Edit mode: load existing deck data
  const { data: decksData } = useQuery({
    queryKey: ["flashcard-decks"],
    queryFn: () => FlashcardDecksService.listDecks(),
    enabled: mode === "edit",
  })
  const deck =
    mode === "edit" ? decksData?.data.find((d) => d.id === deckId) : undefined

  const { data: programsData } = useQuery({
    queryKey: ["programs"],
    queryFn: () => ProgramsService.listPrograms({ limit: 200 }),
    enabled: mode === "edit",
  })

  const { data: subjectsData } = useQuery({
    queryKey: ["subjects"],
    queryFn: () => ContentService.listSubjects({ limit: 100 }),
  })

  const { data: tagSuggestions } = useQuery({
    queryKey: ["flashcard-tags"],
    queryFn: () => FlashcardDecksService.listFlashcardTags(),
  })

  const { data: assignedProgramIds } = useQuery({
    queryKey: ["flashcard-deck-programs", deckId],
    queryFn: () => FlashcardDecksService.listDeckPrograms({ deckId: deckId! }),
    enabled: mode === "edit" && !!deckId,
  })

  const { data: cardsData, isLoading: cardsLoading } = useQuery({
    queryKey: ["flashcard-deck-cards", deckId],
    queryFn: () => FlashcardDecksService.listCards({ deckId: deckId! }),
    enabled: mode === "edit" && !!deckId,
  })

  const [localCardOrder, setLocalCardOrder] = useState<
    FlashcardPublic[] | null
  >(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reset local order when server data changes
  useEffect(() => {
    setLocalCardOrder(null)
  }, [cardsData])

  const displayedCards = localCardOrder ?? cardsData?.data ?? []

  useEffect(() => {
    if (mode !== "edit" || !assignedProgramIds || !programsData?.data) return
    setSelectedPrograms(
      programsData.data
        .filter((p) => assignedProgramIds.includes(p.id))
        .map((p) => ({ id: p.id, name: p.name, status: p.status })),
    )
  }, [assignedProgramIds, programsData?.data, mode])

  useEffect(() => {
    if (mode !== "edit" || !deck) return
    setShuffleCards(deck.shuffle_cards ?? true)
    setDeckTags(deck.tags ?? [])
  }, [deck, mode])

  useEffect(() => {
    if (mode !== "edit" || !deck?.subject_id || !subjectsData?.data) return
    const subject = subjectsData.data.find(
      (item) => item.id === deck.subject_id,
    )
    setDeckSubject(subject ? [{ id: subject.id, name: subject.name }] : [])
  }, [deck?.subject_id, mode, subjectsData?.data])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DeckFormValues>({
    values:
      mode === "edit"
        ? { name: deck?.name ?? "", description: deck?.description ?? "" }
        : undefined,
  })

  // Question bank
  const { data: questionsData, isLoading: questionsLoading } = useQuery({
    queryKey: [
      "flashcard-question-bank",
      {
        search: searchValue,
        subjectIds: selectedSubjects.map((s) => s.id),
        topicIds: selectedTopics.map((t) => t.id),
      },
    ],
    queryFn: () =>
      QuestionsService.listQuestions({
        search: searchValue.trim() || undefined,
        subjectIds:
          selectedSubjects.length > 0
            ? selectedSubjects.map((s) => s.id)
            : undefined,
        topicIds:
          selectedTopics.length > 0
            ? selectedTopics.map((t) => t.id)
            : undefined,
        limit: 20,
      }),
  })

  // Edit mode: immediate card add/delete
  const addCardMutation = useMutation({
    mutationFn: (card: PendingCard) =>
      FlashcardDecksService.addCard({ deckId: deckId!, requestBody: card }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["flashcard-deck-cards", deckId],
      }),
  })

  const deleteCardMutation = useMutation({
    mutationFn: (cardId: string) =>
      FlashcardDecksService.deleteCard({ deckId: deckId!, cardId }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["flashcard-deck-cards", deckId],
      }),
  })

  const reorderCardsMutation = useMutation({
    mutationFn: (cardIds: string[]) =>
      FlashcardDecksService.reorderCards({
        deckId: deckId!,
        requestBody: { card_ids: cardIds },
      }),
    onError: (err: ApiError) => {
      setLocalCardOrder(null)
      handleError(err)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["flashcard-deck-cards", deckId],
      })
    },
  })

  const handleEditModeDragEnd = (result: DropResult) => {
    if (!result.destination) return
    const from = result.source.index
    const to = result.destination.index
    if (from === to) return
    const current = localCardOrder ?? cardsData?.data ?? []
    const next = [...current]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    setLocalCardOrder(next)
    reorderCardsMutation.mutate(next.map((c) => c.id))
  }

  const handleCreateModeDragEnd = (result: DropResult) => {
    if (!result.destination) return
    const from = result.source.index
    const to = result.destination.index
    if (from === to) return
    setPendingCards((prev) => {
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }

  const handleAddQuestion = (question: QuestionPublic) => {
    const card: PendingCard = {
      front: question.question_text,
      back: buildBackFromQuestion(question),
      source_question_id: question.id,
    }
    if (mode === "create") {
      setPendingCards((prev) => [...prev, card])
    } else {
      addCardMutation.mutate(card)
    }
  }

  const isQuestionSelected = (questionId: string) => {
    if (mode === "create") {
      return pendingCards.some((c) => c.source_question_id === questionId)
    }
    return (
      cardsData?.data.some((c) => c.source_question_id === questionId) ?? false
    )
  }

  const handleAddManualCard = () => {
    const front = manualFront.trim()
    const back = manualBack.trim()
    if (!front) {
      setManualError("Front is required.")
      return
    }
    if (
      isUploadingRichTextImage ||
      containsLocalImageUrls(front) ||
      containsLocalImageUrls(back)
    ) {
      setManualError("Wait for images to finish uploading before saving.")
      return
    }
    setManualError("")
    const card: PendingCard = { front, back }
    if (mode === "create") {
      setPendingCards((prev) => [...prev, card])
      setManualFront("")
      setManualBack("")
    } else {
      addCardMutation.mutate(card, {
        onSuccess: () => {
          setManualFront("")
          setManualBack("")
        },
      })
    }
  }

  // Create mode submit
  const createMutation = useMutation({
    mutationFn: async (values: DeckFormValues) => {
      const created = await FlashcardDecksService.createDeck({
        requestBody: {
          name: values.name,
          description: values.description || null,
          subject_id: deckSubject[0]?.id ?? null,
          tags: deckTags,
          shuffle_cards: shuffleCards,
        },
      })
      if (selectedPrograms.length > 0) {
        await FlashcardDecksService.assignPrograms({
          deckId: created.id,
          requestBody: { program_ids: selectedPrograms.map((p) => p.id) },
        })
      }
      await Promise.all(
        pendingCards.map((card) =>
          FlashcardDecksService.addCard({
            deckId: created.id,
            requestBody: card,
          }),
        ),
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flashcard-decks"] })
      navigate({ to: "/flashcards" })
    },
  })

  // Edit mode submit
  const editMutation = useMutation({
    mutationFn: async (values: DeckFormValues) => {
      await FlashcardDecksService.updateDeck({
        deckId: deckId!,
        requestBody: {
          name: values.name,
          description: values.description || null,
          subject_id: deckSubject[0]?.id ?? null,
          tags: deckTags,
          shuffle_cards: shuffleCards,
        },
      })

      const previousIds = new Set(assignedProgramIds ?? [])
      const nextIds = selectedPrograms.map((p) => p.id)
      const nextIdSet = new Set(nextIds)
      const idsToAdd = nextIds.filter((id) => !previousIds.has(id))
      const idsToRemove = [...previousIds].filter((id) => !nextIdSet.has(id))

      if (idsToAdd.length > 0) {
        await FlashcardDecksService.assignPrograms({
          deckId: deckId!,
          requestBody: { program_ids: idsToAdd },
        })
      }
      await Promise.all(
        idsToRemove.map((programId) =>
          FlashcardDecksService.removeProgram({ deckId: deckId!, programId }),
        ),
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["flashcard-decks"] })
      queryClient.invalidateQueries({
        queryKey: ["flashcard-deck-programs", deckId],
      })
      navigate({ to: "/flashcards" })
    },
  })

  const onSubmit = (values: DeckFormValues) => {
    if (
      isUploadingRichTextImage ||
      pendingCards.some(
        (card) =>
          containsLocalImageUrls(card.front) ||
          containsLocalImageUrls(card.back),
      )
    ) {
      setManualError("Wait for images to finish uploading before saving.")
      return
    }
    setManualError("")
    if (mode === "create") return createMutation.mutateAsync(values)
    return editMutation.mutateAsync(values)
  }

  const isSaving =
    isSubmitting ||
    createMutation.isPending ||
    editMutation.isPending ||
    isUploadingRichTextImage
  const hasError = createMutation.isError || editMutation.isError

  return (
    <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap={6}>
      {/* Left column: settings + cards */}
      <GridItem>
        <VStack gap={6} align="stretch">
          {/* Deck settings */}
          <Box borderWidth="1px" borderRadius="md" p={4}>
            <Heading size="md" mb={4}>
              {mode === "create" ? "New Deck" : "Deck Settings"}
            </Heading>
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack gap={4} align="stretch">
                <Field
                  label="Name"
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                >
                  <Input
                    {...register("name", { required: "Name is required" })}
                    placeholder="e.g. Cardiology Essentials"
                  />
                </Field>
                <Field label="Description">
                  <Textarea
                    {...register("description")}
                    placeholder="Optional description"
                  />
                </Field>
                <Field label="Program assignments">
                  <ProgramSelector
                    selectedPrograms={selectedPrograms}
                    onChange={setSelectedPrograms}
                  />
                </Field>
                <Field label="Subject">
                  <SubjectSelector
                    selectedSubjects={deckSubject}
                    onChange={(subjects) => setDeckSubject(subjects.slice(-1))}
                  />
                </Field>
                <Field label="Tags">
                  <TagInput
                    tags={deckTags}
                    suggestions={tagSuggestions ?? []}
                    onChange={setDeckTags}
                  />
                </Field>
                <HStack justify="space-between" align="center">
                  <Box>
                    <Text fontWeight="medium" fontSize="sm">
                      Shuffle cards during study
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      When off, cards appear in the order you arranged them
                    </Text>
                  </Box>
                  <ChakraSwitch.Root
                    checked={shuffleCards}
                    onCheckedChange={({ checked }) => setShuffleCards(checked)}
                  >
                    <ChakraSwitch.HiddenInput />
                    <ChakraSwitch.Control>
                      <ChakraSwitch.Thumb />
                    </ChakraSwitch.Control>
                  </ChakraSwitch.Root>
                </HStack>
                {hasError && (
                  <Text color="red.500" fontSize="sm">
                    {mode === "create"
                      ? "Failed to create deck. Please try again."
                      : "Failed to save changes."}
                  </Text>
                )}
                <Button type="submit" disabled={isSaving}>
                  {mode === "create" ? "Create Deck" : "Save Changes"}
                </Button>
              </VStack>
            </form>
          </Box>

          {/* Cards list + manual add */}
          <Box borderWidth="1px" borderRadius="md" p={4}>
            {mode === "create" ? (
              <>
                <Heading size="md" mb={4}>
                  Cards ({pendingCards.length})
                </Heading>
                {pendingCards.length > 0 && (
                  <DragDropContext onDragEnd={handleCreateModeDragEnd}>
                    <StrictModeDroppable droppableId="pending-cards">
                      {(provided: DroppableProvided) => (
                        <VStack
                          gap={3}
                          align="stretch"
                          mb={4}
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {pendingCards.map((card, idx) => (
                            <Draggable
                              key={`pending-${idx}`}
                              draggableId={`pending-${idx}`}
                              index={idx}
                            >
                              {(
                                dragProvided: DraggableProvided,
                                snapshot: DraggableStateSnapshot,
                              ) => (
                                <Box
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  p={3}
                                  borderWidth="1px"
                                  borderRadius="md"
                                  bg={
                                    snapshot.isDragging ? "blue.50" : undefined
                                  }
                                  _dark={{
                                    bg: snapshot.isDragging
                                      ? "blue.900"
                                      : undefined,
                                  }}
                                >
                                  <HStack justify="space-between" align="start">
                                    <Box
                                      {...dragProvided.dragHandleProps}
                                      color="gray.400"
                                      cursor="grab"
                                      flexShrink={0}
                                      pt={1}
                                    >
                                      <MdDragHandle />
                                    </Box>
                                    <Box flex={1} minW={0}>
                                      <Box
                                        fontSize="sm"
                                        fontWeight="medium"
                                        mb={1}
                                        lineClamp={2}
                                      >
                                        <FlashcardContent
                                          content={card.front}
                                        />
                                      </Box>
                                      <Box
                                        fontSize="xs"
                                        color="gray.500"
                                        lineClamp={1}
                                      >
                                        <FlashcardContent content={card.back} />
                                      </Box>
                                    </Box>
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      colorPalette="red"
                                      onClick={() =>
                                        setPendingCards((prev) =>
                                          prev.filter((_, i) => i !== idx),
                                        )
                                      }
                                    >
                                      ×
                                    </Button>
                                  </HStack>
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </VStack>
                      )}
                    </StrictModeDroppable>
                  </DragDropContext>
                )}
              </>
            ) : (
              <>
                <HStack justify="space-between" align="center" mb={4}>
                  <Heading size="md">Cards ({cardsData?.count ?? 0})</Heading>
                  {deckId && <FlashcardBulkUploadDialog deckId={deckId} />}
                </HStack>
                {cardsLoading ? (
                  <Text color="gray.500" fontSize="sm" mb={4}>
                    Loading cards...
                  </Text>
                ) : displayedCards.length > 0 ? (
                  <DragDropContext onDragEnd={handleEditModeDragEnd}>
                    <StrictModeDroppable droppableId="edit-cards">
                      {(provided: DroppableProvided) => (
                        <VStack
                          gap={3}
                          align="stretch"
                          mb={4}
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {displayedCards.map((card, index) => (
                            <Draggable
                              key={card.id}
                              draggableId={card.id}
                              index={index}
                            >
                              {(
                                dragProvided: DraggableProvided,
                                snapshot: DraggableStateSnapshot,
                              ) => (
                                <Box
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  p={3}
                                  borderWidth="1px"
                                  borderRadius="md"
                                  bg={
                                    snapshot.isDragging ? "blue.50" : undefined
                                  }
                                  _dark={{
                                    bg: snapshot.isDragging
                                      ? "blue.900"
                                      : undefined,
                                  }}
                                >
                                  <HStack justify="space-between" align="start">
                                    <Box
                                      {...dragProvided.dragHandleProps}
                                      color="gray.400"
                                      cursor="grab"
                                      flexShrink={0}
                                      pt={1}
                                    >
                                      <MdDragHandle />
                                    </Box>
                                    <Box flex={1} minW={0}>
                                      <Box
                                        fontSize="sm"
                                        fontWeight="medium"
                                        mb={1}
                                        lineClamp={2}
                                      >
                                        <FlashcardContent
                                          content={card.front}
                                        />
                                      </Box>
                                      <Box
                                        fontSize="xs"
                                        color="gray.500"
                                        lineClamp={1}
                                      >
                                        <FlashcardContent content={card.back} />
                                      </Box>
                                    </Box>
                                    <HStack gap={1} flexShrink={0}>
                                      <Link
                                        to={`/flashcards/${deckId}/cards/${card.id}/edit`}
                                      >
                                        <Button size="xs" variant="ghost">
                                          Edit
                                        </Button>
                                      </Link>
                                      <Button
                                        size="xs"
                                        variant="ghost"
                                        colorPalette="red"
                                        onClick={() =>
                                          deleteCardMutation.mutate(card.id)
                                        }
                                        disabled={deleteCardMutation.isPending}
                                      >
                                        ×
                                      </Button>
                                    </HStack>
                                  </HStack>
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </VStack>
                      )}
                    </StrictModeDroppable>
                  </DragDropContext>
                ) : null}
              </>
            )}

            {/* Manual card add form */}
            <Box borderTopWidth="1px" mb={4} />
            <Heading size="sm" mb={3}>
              Add Card Manually
            </Heading>
            <VStack gap={3} align="stretch">
              <Field label="Front (question / prompt)">
                <RichTextEditor
                  value={manualFront}
                  onChange={setManualFront}
                  onUploadStateChange={handleRichTextUploadStateChange}
                  onUploadError={setManualError}
                  placeholder="Front of card — paste screenshots or type here"
                  minHeight="100px"
                />
              </Field>
              <Field label="Back (answer / rationale)">
                <RichTextEditor
                  value={manualBack}
                  onChange={setManualBack}
                  onUploadStateChange={handleRichTextUploadStateChange}
                  onUploadError={setManualError}
                  placeholder="Back of card — paste screenshots or type here"
                  minHeight="100px"
                />
              </Field>
              {manualError && (
                <Text color="red.500" fontSize="sm">
                  {manualError}
                </Text>
              )}
              <Button
                size="sm"
                onClick={handleAddManualCard}
                disabled={addCardMutation.isPending}
              >
                Add Card
              </Button>
            </VStack>
          </Box>
        </VStack>
      </GridItem>

      {/* Right column: question bank */}
      <GridItem>
        <QuizQuestionBank
          loading={questionsLoading}
          availableQuestions={questionsData?.data ?? []}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          selectedSubjects={selectedSubjects}
          onSubjectsChange={setSelectedSubjects}
          selectedTopics={selectedTopics}
          onTopicsChange={setSelectedTopics}
          isQuestionSelected={isQuestionSelected}
          onAddQuestion={handleAddQuestion}
          onPreviewQuestion={(questionId: string) => {
            setPreviewQuestionId(questionId)
            setIsPreviewOpen(true)
          }}
        />
      </GridItem>
      <QuestionPreviewModal
        questionId={previewQuestionId}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </Grid>
  )
}

type StrictModeDroppableProps = {
  children: (provided: DroppableProvided) => ReactNode
} & Omit<Parameters<typeof Droppable>[0], "children">

function StrictModeDroppable({
  children,
  ...droppableProps
}: StrictModeDroppableProps) {
  const [enabled, setEnabled] = useState(false)
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true))
    return () => cancelAnimationFrame(animation)
  }, [])
  if (!enabled) return null
  return (
    <Droppable {...droppableProps}>
      {(provided) => children(provided)}
    </Droppable>
  )
}
